import {
  Button,
  Divider,
  Menu,
  MenuDivider,
  MenuItem,
  NonIdealState,
  Popover,
  Tag,
} from '@blueprintjs/core';
import noEmailSVG from '../../../assets/noEmails.svg';
import { useEmailState } from './useEmailState';
import {
  lock_hooks,
  useApplicationDetail,
  useCustomer,
  email_communication_template_hooks,
  email_communication_hooks,
  useGetConsentPartners,
  useGetConsentItemsByCustomer,
} from '@axo/shared/data-access/hooks';
import { EmailCardView } from './EmailCardView';
import { EmailCardEdit } from './EmailCardEdit';
import panelClasses from '../Panel/Panel.module.css';
import { CommunicationCount } from '../Panel';
import { useState } from 'react';
import { OrttoEmailCard } from './OrttoEmailCard.tsx';
import styles from './EmailPanel.module.css';
import { email } from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/types';

enum EmailType {
  ACE = 'ACE',
  ORTTO = 'ORTTO',
}

export function EmailPanel({ applicationID }: { applicationID: string }) {
  const aceEmailListState = useEmailState(applicationID);
  const application = useApplicationDetail(applicationID);
  const customer = useCustomer(application.data?.CustomerID);
  const emailTemplates = email_communication_template_hooks.useEmailTemplates(
    application.data?.MarketCountry ?? '',
    application.data?.Meta?.Broker ?? 0,
  );
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    application.data?.MarketCountry,
  );
  const [emailType, setEmailType] = useState(EmailType.ACE);
  const orttoEmails = email_communication_hooks.useOrttoEmails(
    customer.data?.ID,
    application.data?.MarketCountry,
  );

  const getTemplate = (templateID?: string) => {
    if (!emailTemplates.data || !templateID) return;
    return emailTemplates.data.Items.find((x) => x.ID === templateID);
  };

  const consentPartners = useGetConsentPartners();
  const partnerID = consentPartners.data?.find(
    (x) =>
      x.BrandID === application.data?.Meta?.Brand &&
      Boolean(application.data.Meta.Brand),
  )?.ID;
  const consentItems = useGetConsentItemsByCustomer(
    application.data?.MarketCountry as MarketCountryCode,
    application.data?.CustomerID,
  );

  const getTemplateStatus = (_: email.EmailTemplate) => {
    // FIXME: This is a hotfix until correct behavior is determined
    return { disabled: false, text: '' };

    if (
      consentItems.data?.Items?.find(
        (x) =>
          x.PartnerID === partnerID &&
          x.Country === application.data?.MarketCountry &&
          x.Transactional &&
          x.Channel === 'email' &&
          x.State === 'given',
      )
    )
      return { disabled: false, text: 'Consent given for this template' };

    return {
      disabled: true,
      text: 'Missing consent or template not allowed',
    };
  };

  return (
    <div className={panelClasses.panel}>
      <div className={styles.tags}>
        <Tag
          round
          minimal={emailType !== EmailType.ACE}
          interactive
          large
          intent={emailType === EmailType.ACE ? 'primary' : 'none'}
          onClick={() => setEmailType(EmailType.ACE)}
        >
          ACE
        </Tag>
        <Tag
          round
          minimal={emailType !== EmailType.ORTTO}
          interactive
          large
          intent={emailType === EmailType.ORTTO ? 'primary' : 'none'}
          onClick={() => setEmailType(EmailType.ORTTO)}
        >
          Ortto
        </Tag>
      </div>
      {emailType === EmailType.ACE && (
        <>
          <CommunicationCount
            label="Number of ACE Emails sent in total:"
            count={aceEmailListState.state.length}
          />

          <div className={panelClasses.panelActions}>
            <Popover
              interactionKind="hover"
              content={
                <Menu>
                  <MenuDivider title="Select a template" />
                  {emailTemplates.data?.Items.map((template) => (
                    <MenuItem
                      disabled={
                        getTemplateStatus(template).disabled || !hasLock.data
                      }
                      key={template.ID}
                      text={template.Name}
                      onClick={() => aceEmailListState.addNewEmail(template)}
                    />
                  ))}
                </Menu>
              }
            >
              <Button
                disabled={!hasLock.data}
                icon="envelope"
                text="New Email"
              />
            </Popover>
          </div>
          <Divider />

          <div className={panelClasses.cardContainer}>
            {!aceEmailListState.hasLoaded ? (
              <NonIdealState>
                <p>Loading emails...</p>
              </NonIdealState>
            ) : null}

            {aceEmailListState.hasLoaded && !aceEmailListState.state.length ? (
              <NonIdealState icon={<img src={noEmailSVG} alt="No emails" />}>
                <p>There are no emails added yet.</p>
              </NonIdealState>
            ) : null}

            {[
              ...aceEmailListState.state.filter((x) => x.isNew),
              ...aceEmailListState.state.filter((x) => !x.isNew),
            ].map((email) => {
              const template = getTemplate(email.email.TemplateID);
              if (email.isNew && application.data?.MarketCountry) {
                return (
                  <EmailCardEdit
                    key={email.email.ID}
                    email={email.email}
                    to={customer.data?.Email || ''}
                    setEmail={aceEmailListState.editEmail(email)}
                    saveEmail={() => aceEmailListState.saveEmail(email)}
                    cancelEmail={() => aceEmailListState.cancelEmail(email)}
                    template={template}
                    applicationID={applicationID}
                    marketCountry={application.data.MarketCountry}
                  />
                );
              }

              if (email.email !== null && !email.isNew) {
                return (
                  <EmailCardView
                    key={email.email.ID}
                    email={email.email}
                    to={customer.data?.Email || ''}
                    template={template}
                  />
                );
              }

              return null;
            })}
          </div>
        </>
      )}
      {emailType === EmailType.ORTTO && (
        <>
          <CommunicationCount
            label="Number of Ortto Emails sent in total:"
            count={orttoEmails.data?.Items?.length ?? 0}
          />
          <Divider />
          <div className={panelClasses.cardContainer}>
            {orttoEmails.isLoading ? (
              <NonIdealState>
                <p>Loading ortto emails...</p>
              </NonIdealState>
            ) : null}

            {!orttoEmails.isLoading && !orttoEmails.data?.Items.length ? (
              <NonIdealState
                icon={<img src={noEmailSVG} alt="No ortto emails" />}
              >
                <p>There are no ortto emails added yet.</p>
              </NonIdealState>
            ) : null}
            {orttoEmails.data &&
              orttoEmails.data.Items.map((orttoEmail) => {
                return (
                  <OrttoEmailCard key={orttoEmail.ID} email={orttoEmail} />
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}
